// src/components/BaseLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserAvatarDropdown from './UserAvatarDropdown';
import NotificationBell from './NotificationBell';
import EmailVerificationBanner from './EmailVerificationBanner';
import { auth } from '../lib/firebase';
import {
  Home,
  Video,
  CircleDot,
  Newspaper,
  Paintbrush,
  User,
  List,
  Settings,
  Sun,
  Moon,
  BarChart2,
} from 'lucide-react';
import { getVideos, getVideoRoute } from '../lib/services/videoService';
import type { Video as VideoType } from '../lib/services/videoService';
import { logRender } from '../utils/debugLoop';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrentUser } from '../contexts/UserContext';
import { useSessionHeartbeat } from '../hooks/useSessionHeartbeat';
import LiveToastContainer from '../live/components/LiveToastContainer';
import { useActiveLiveRoom } from '../contexts/ActiveLiveRoomContext';

export default function BaseLayout({ children }: { children: React.ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<VideoType[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useTheme();
  const { currentUser } = useCurrentUser();
  useSessionHeartbeat(currentUser?.uid ?? null);
  const { t } = useTranslation('common');
  const { isHostingLive, activeRoomId } = useActiveLiveRoom();
  const [showLiveWarning, setShowLiveWarning] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);

  // Prevent accidental browser close/refresh while hosting
  useEffect(() => {
    if (!isHostingLive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isHostingLive]);

  logRender('BaseLayout', undefined, { menuOpen, isLoggedIn, search, searchLoading, showResults });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsLoggedIn(!!user && !user.isAnonymous);
    });
    return () => unsubscribe();
  }, []);

  // Search effect
  useEffect(() => {
    if (search.trim().length === 0) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setSearchLoading(true);
    getVideos().then((videos) => {
      const filtered = videos.filter((v) =>
        v.title.toLowerCase().includes(search.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 8));
      setShowResults(true);
      setSearchLoading(false);
    });
  }, [search]);

  // Hide results + close menu on navigation
  useEffect(() => {
    setShowResults(false);
    setMenuOpen(false);
  }, [location.pathname]);

  // Close menu on click outside
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        menuButtonRef.current && !menuButtonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Navigation guard for hosts in active live rooms
  const guardedNavigate = (path: string) => {
    // If host is in a live room and navigating away from it, show warning
    if (isHostingLive && activeRoomId && !path.includes(`/live/${activeRoomId}`)) {
      setPendingNavPath(path);
      setShowLiveWarning(true);
      return;
    }
    navigate(path);
  };

  const handleConfirmLeave = () => {
    setShowLiveWarning(false);
    if (pendingNavPath) {
      navigate(pendingNavPath);
      setPendingNavPath(null);
    }
  };

  const handleCancelLeave = () => {
    setShowLiveWarning(false);
    setPendingNavPath(null);
  };

  const isTwinBoost = location.pathname.startsWith('/studio/twinboost');

  // Enable scrolling for all pages except Editor2 + TwinBoost workspace
  useEffect(() => {
    const isEditor2 = location.pathname === '/editor2';

    if (isEditor2 || isTwinBoost) {
      // Remove allow-scroll classes for Editor2/TwinBoost workspaces
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
      document.getElementById('root')?.classList.remove('allow-scroll');
    } else {
      // Add allow-scroll classes for all other pages
      document.documentElement.classList.add('allow-scroll');
      document.body.classList.add('allow-scroll');
      document.getElementById('root')?.classList.add('allow-scroll');
    }
  }, [location.pathname]);

  if (isTwinBoost) {
    return (
      <div className="min-h-screen bg-[#0b0d0f] text-zinc-100">
        {children}
        <LiveToastContainer />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ color: 'var(--vb-text-primary)' }}>
      {/* Header - Fixed at top (hidden on landing page when not logged in) */}
      {!(location.pathname === '/' && !isLoggedIn) && <header
        className="fixed top-0 left-0 right-0 flex items-center justify-between px-4 py-2 shadow-md backdrop-blur-sm z-50"
        style={{ backgroundColor: 'var(--vb-bg-primary)', opacity: 0.98 }}
      >
        {/* Left: Menu + Logo */}
        <div className="flex items-center space-x-4 flex-shrink-0">
          {isLoggedIn && (
            <button
              ref={menuButtonRef}
              onClick={() => setMenuOpen((prev) => !prev)}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold px-3 py-1 rounded-lg shadow hover:from-yellow-300 hover:to-orange-400 hover:scale-105 hover:shadow-lg transition-all duration-200 text-sm"
            >
              {t('nav.menu')}
            </button>
          )}

          <div className="flex items-center space-x-2">
            <img src="/images/viewsboost-logo.png" alt="Logo" className="h-8 w-8" />
            <span className="text-lg font-bold text-yellow-400 hidden sm:block">{t('branding.appName')}</span>
          </div>
        </div>

        {/* Center: Search Bar (visible on dashboard) */}
        {location.pathname === '/dashboard' && (
          <div className="flex-1 max-w-xl mx-4 relative">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('search.placeholder')}
              className="w-full px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-400 text-sm"
              style={{
                backgroundColor: 'var(--vb-bg-secondary)',
                color: 'var(--vb-text-primary)',
              }}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              autoComplete="off"
            />
            {showResults && (
              <div
                className="absolute top-12 left-0 right-0 rounded-lg shadow-lg border overflow-hidden z-40"
                style={{
                  backgroundColor: 'var(--vb-bg-secondary)',
                  borderColor: 'var(--vb-border)',
                }}
              >
                {searchLoading ? (
                  <div className="p-4 text-center" style={{ color: 'var(--vb-text-muted)' }}>{t('status.searching')}</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center" style={{ color: 'var(--vb-text-muted)' }}>{t('errors.noResults')}</div>
                ) : (
                  <ul>
                    {searchResults.map((v) => (
                      <li
                        key={v.id}
                        className="flex items-center gap-3 px-4 py-2 cursor-pointer transition"
                        style={{ color: 'var(--vb-text-primary)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--vb-bg-hover)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => {
                          navigate(getVideoRoute(v));
                          setShowResults(false);
                          setSearch('');
                        }}
                      >
                        <img
                          src={v.thumbnail || `/images/video-thumb-placeholder.png`}
                          alt={v.title}
                          className="w-14 h-8 object-cover rounded"
                        />
                        <span className="truncate text-sm">{v.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Right: Theme Toggle + Avatar */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--vb-text-secondary)' }}
            aria-label={darkMode ? t('theme.switchToLight') : t('theme.switchToDark')}
          >
            {darkMode ? (
              <Sun size={20} className="text-yellow-400" />
            ) : (
              <Moon size={20} />
            )}
          </button>
          {isLoggedIn && (
            <>
              <NotificationBell />
              <UserAvatarDropdown />
            </>
          )}
        </div>

        {/* Sidebar Dropdown Menu */}
        {menuOpen && isLoggedIn && (
          <div
            ref={menuRef}
            className="absolute top-14 left-4 rounded-xl shadow-lg w-56 p-2 z-50 space-y-1"
            style={{ backgroundColor: 'var(--vb-bg-secondary)' }}
          >
            <MenuItem icon={<Home size={20} />} label={t('nav.home')} path="/dashboard" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<Video size={20} />} label={t('nav.shorts')} path="/shorts" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<CircleDot size={20} className="text-red-500" />} label={t('nav.live')} path="/live" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} highlight />
            <MenuItem icon={<Newspaper size={20} />} label={t('nav.news')} path="/news" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<Paintbrush size={20} />} label={t('nav.studio')} path="/studio" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<User size={20} />} label={t('nav.profile')} path={currentUser ? `/profile/${currentUser.uid}` : '/profile'} navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<List size={20} />} label={t('nav.aura')} path="/aura" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<BarChart2 size={20} className="text-blue-400" />} label="Analytics" path="/analytics" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
            <MenuItem icon={<Settings size={20} />} label={t('nav.settings')} path="/settings" navigate={guardedNavigate} onClose={() => setMenuOpen(false)} />
          </div>
        )}
      </header>}

      {/* T7 (LAUNCH_PLAN.md): passive verification banner. Self-hides for
          signed-out, anonymous, Google (auto-verified), already-verified, and
          dismissed states — so the layout cost is zero in those cases. */}
      <div className={location.pathname === '/' && !isLoggedIn ? '' : 'pt-14'}>
        <EmailVerificationBanner />
      </div>

      {/* Main Page Content - No offset on landing page when not logged in */}
      <main className={location.pathname === '/' && !isLoggedIn ? '' : 'p-4'}>{children}</main>

      {/* Live Room Navigation Warning Modal */}
      {showLiveWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
                <CircleDot className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg">Leave Live Room?</h3>
                <p className="text-zinc-400 text-sm">You are currently hosting a live room</p>
              </div>
            </div>
            <p className="text-zinc-300 text-sm mb-6">
              Navigating away will end your live session and disconnect all participants. Are you sure you want to leave?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelLeave}
                className="flex-1 px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg font-medium transition-colors"
              >
                Stay in Live
              </button>
              <button
                onClick={handleConfirmLeave}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                Leave & End Live
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Toast Notifications */}
      <LiveToastContainer />
    </div>
  );
}

function MenuItem({
  icon,
  label,
  path,
  navigate,
  onClose,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  path: string;
  navigate: (path: string) => void;
  onClose: () => void;
  highlight?: boolean;
}) {
  return (
    <button
      onClick={() => {
        navigate(path);
        onClose();
      }}
      className="flex items-center space-x-3 hover:text-yellow-400 hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 w-full text-left"
      style={{ color: 'var(--vb-text-primary)' }}
    >
      <span className={highlight ? 'text-red-500' : ''}>{icon}</span>
      <span className={highlight ? 'text-yellow-400 font-semibold' : ''}>{label}</span>
    </button>
  );
}
